import type { Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { ToggleControl, type ToggleOptions } from "./ToggleControl.js";

export interface RadioButtonOptions extends ToggleOptions {
  /**
   * Radios sharing a group name are mutually exclusive. Omit it to use the
   * WinForms default: every radio directly inside the same container forms
   * one group.
   */
  group?: string;
}

/** A mutually exclusive option button. */
export class RadioButton extends ToggleControl {
  group: string | null;

  constructor(options: RadioButtonOptions = {}) {
    super(options);
    this.group = options.group ?? null;
    // A radio constructed checked still has to clear its siblings.
    if (this.checked) queueMicrotask(() => this.#clearSiblings());
  }

  /** Sibling radios in the same container that share this radio's group. */
  siblings(): RadioButton[] {
    const parent = this.parent;
    if (!parent) return [];
    const out: RadioButton[] = [];
    for (const c of parent.controls) {
      if (c === this) continue;
      if (c instanceof RadioButton && c.group === this.group) out.push(c);
    }
    return out;
  }

  protected override setChecked(value: boolean): void {
    if (this.checked === value) return;
    this.checkedValue = value;
    if (value) this.#clearSiblings();
    this.emit("change");
    this.invalidate();
  }

  #clearSiblings(): void {
    for (const s of this.siblings()) {
      if (!s.checked) continue;
      s.checkedValue = false;
      s.emit("change");
      s.invalidate();
    }
  }

  /** Clicking selects; unlike a check box it never unselects. */
  protected override toggle(): void {
    this.checked = true;
  }

  protected override boxSize(theme: Theme): number {
    return theme.metrics.radioSize;
  }

  protected override drawBox(rc: RenderContext, r: Rect, theme: Theme, s: VisualState): void {
    theme.drawRadioButton(rc, r, s);
  }
}
